<script setup>
import { ref, onMounted, watch } from 'vue';
import { useRoute } from 'vue-router';
import { useI18n } from 'vue-i18n';
import API from 'dashboard/api/shopifyOrderActions';
import Button from 'dashboard/components-next/button/Button.vue';
import OrderActionSummary from 'dashboard/components-next/shopify/OrderActionSummary.vue';
import OrderActionConfirmation from 'dashboard/components-next/shopify/OrderActionConfirmation.vue';

const { t } = useI18n();
const route = useRoute();
const confirmation = ref(null);
const rows = ref([]);
const canApprove = ref(false);
const busy = ref(false);
const error = ref('');
const page = ref(1);
const filter = ref('');
const declineReasons = ref({});
const statuses = [
  'pending',
  'approved',
  'executing',
  'succeeded',
  'declined',
  'failed',
  'needs_review',
];
const run = async operation => {
  busy.value = true;
  error.value = '';
  try {
    await operation();
  } catch (e) {
    error.value = e.response?.data?.error || t('ORDER_ACTIONS.ERROR');
  } finally {
    busy.value = false;
  }
};
const fetchRows = async () => {
  const { data } = await API.get({ page: page.value, status: filter.value });
  rows.value = data.actions;
  canApprove.value = data.can_approve === true;
};
const load = () => run(fetchRows);
const approve = action =>
  run(async () => {
    await API.approve(action.id);
    await fetchRows();
  });
const decline = action =>
  run(async () => {
    await API.decline(action.id, declineReasons.value[action.id]);
    await fetchRows();
  });
const move = offset => {
  page.value += offset;
  load();
};
watch(filter, () => {
  page.value = 1;
  load();
});
watch(
  () => route.params.accountId,
  () => {
    page.value = 1;
    canApprove.value = false;
    rows.value = [];
    load();
  }
);
onMounted(load);
</script>

<template>
  <div
    class="flex h-full min-w-0 w-full flex-col gap-4 overflow-auto break-words p-4 text-n-slate-12 sm:p-6"
  >
    <div class="flex flex-wrap items-center justify-between gap-4">
      <h1 class="text-xl font-semibold">{{ t('ORDER_ACTIONS.TITLE') }}</h1>
      <Button
        :label="t('ORDER_ACTIONS.REFRESH')"
        :disabled="busy"
        @click="load"
      />
    </div>
    <p v-if="error" role="alert" class="text-n-ruby-11">{{ error }}</p>
    <p>{{ t('ORDER_ACTIONS.POLICY_NOTE') }}</p>
    <label
      >{{ t('ORDER_ACTIONS.STATUS') }}
      <select v-model="filter" :disabled="busy">
        <option value="">{{ t('ORDER_ACTIONS.ALL') }}</option>
        <option v-for="status in statuses" :key="status" :value="status">
          {{ t('ORDER_ACTIONS.STATUS_' + status.toUpperCase()) }}
        </option>
      </select>
    </label>
    <p v-if="!rows.length && !busy">{{ t('ORDER_ACTIONS.EMPTY') }}</p>
    <article
      v-for="action in rows"
      :key="action.id"
      class="flex flex-col gap-3 rounded-xl border border-n-weak p-4"
    >
      <div class="flex flex-wrap items-center justify-between gap-2">
        <strong
          >#{{ action.id }} —
          {{ t('ORDER_ACTIONS.STATUS_' + action.status.toUpperCase()) }}</strong
        >
        <span
          >{{ t('ORDER_ACTIONS.REQUESTED_BY') }}:
          {{ action.requester_name }}</span
        >
        <RouterLink
          v-if="action.conversation_id"
          :to="{
            name: 'inbox_conversation',
            params: {
              accountId: route.params.accountId,
              conversation_id: action.conversation_id,
            },
          }"
        >
          {{ t('ORDER_ACTIONS.CONVERSATION') }}
        </RouterLink>
      </div>
      <OrderActionSummary :action="action" />
      <p v-if="action.result.message">{{ action.result.message }}</p>
      <p v-if="action.status === 'needs_review'" class="text-n-amber-11">
        {{ t('ORDER_ACTIONS.UNKNOWN') }}
      </p>
      <div
        v-if="canApprove && ['pending', 'approved'].includes(action.status)"
        class="flex min-w-0 flex-col gap-3 sm:flex-row sm:flex-wrap sm:items-center"
      >
        <Button
          :label="
            t(
              action.status === 'approved'
                ? 'ORDER_ACTIONS.RETRY_QUEUE'
                : 'ORDER_ACTIONS.APPROVE'
            )
          "
          :disabled="busy"
          @click="confirmation.open(action, true)"
        />
        <template v-if="action.status === 'pending'">
          <input
            v-model="declineReasons[action.id]"
            type="text"
            class="w-full min-w-0 sm:w-auto sm:flex-1"
            :placeholder="t('ORDER_ACTIONS.DECLINE_REASON')"
            maxlength="500"
            :disabled="busy"
          />
          <Button
            :label="t('ORDER_ACTIONS.DECLINE')"
            color="ruby"
            variant="outline"
            :disabled="busy"
            @click="decline(action)"
          />
        </template>
      </div>
      <details>
        <summary class="cursor-pointer">{{ t('ORDER_ACTIONS.AUDIT') }}</summary>
        <div
          v-for="(event, index) in action.events"
          :key="index"
          class="my-2 text-sm"
        >
          {{ new Date(event.created_at).toLocaleString('en-GB') }} —
          {{ event.actor_name }} — {{ event.event_type.replaceAll('_', ' ') }}
          <span v-if="event.details.reason">: {{ event.details.reason }}</span>
        </div>
      </details>
    </article>
    <div class="flex gap-3">
      <Button
        :label="t('ORDER_ACTIONS.PREVIOUS')"
        :disabled="busy || page === 1"
        @click="move(-1)"
      />
      <Button
        :label="t('ORDER_ACTIONS.NEXT')"
        :disabled="busy || rows.length < 30"
        @click="move(1)"
      />
    </div>
    <OrderActionConfirmation
      ref="confirmation"
      :busy="busy"
      @confirm="approve"
    />
  </div>
</template>
